import type { PaginatedReport } from '@shared/report-pagination.ts';
import { useReportPaging, ReportPagingBoundary, ReportPager } from '@web/components/report-pagination.tsx';
import { createContext, useContext, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiRequestError } from '@web/api/client.ts';
import { useSession } from '@web/state/session.tsx';
import { Alert, Button, LoadingBlock, classes } from '@web/components/ui.tsx';
import { formatCompanyInstant } from '@web/i18n/datetime.ts';
import { PLANS, isPlanId, minimumPlanFor, type PlanFeature } from '@shared/constants/index.ts';
import type { Company } from '@shared/types/index.ts';
import { companyHasPlanFeature } from '@web/components/plan-gate.tsx';
import { companyDoesCommercialWork } from '@web/components/commercial-gate.tsx';

/** Existing report endpoints and access rules, organized by the question they answer. */
export const SECTIONS = [
  'money-received',
  'quality-response',
  'cancellations',
  'add-ons',
  'profitability',
  'jobProfit',
  'jobCosting',
  'retention',
  'funnel',
  'receivables',
  'crew',
  'capacity',
  'leads',
] as const;
export type Section = (typeof SECTIONS)[number];

/** The plan feature each tab needs; null = part of every plan. */
export const SECTION_FEATURES: Readonly<Record<Section, PlanFeature | null>> = {
  'money-received': null,
  'quality-response': 'reports_analytics',
  cancellations: 'reports_core',
  'add-ons': 'reports_core',
  profitability: 'reports_analytics',
  jobProfit: 'profit_insights',
  jobCosting: 'reports_advanced',
  retention: 'reports_advanced',
  funnel: 'reports_analytics',
  receivables: null,
  crew: 'reports_analytics',
  capacity: 'reports_advanced',
  leads: 'reports_analytics',
};

/** The sections the shared date range actually filters. */
export const RANGE_SECTIONS: ReadonlySet<Section> = new Set([
  'money-received',
  'quality-response',
  'cancellations',
  'add-ons',
  'profitability',
  'jobProfit',
  'jobCosting',
  'funnel',
  'crew',
  'leads',
]);

export function isSection(value: string | null): value is Section {
  return value !== null && (SECTIONS as readonly string[]).includes(value);
}

export const REPORT_GROUPS = {
  money: ['money-received', 'receivables', 'profitability', 'add-ons', 'jobProfit', 'jobCosting'],
  customers: ['funnel', 'retention', 'leads'],
  team: ['crew', 'capacity'],
  quality: ['quality-response', 'cancellations'],
} as const satisfies Record<string, readonly Section[]>;
export type ReportGroup = keyof typeof REPORT_GROUPS;

/**
 * The sections that only mean something to a company doing commercial work.
 *
 * Job costing asks one question — which of these standing contracts is still
 * profitable — and answers it per site per month against a loaded hourly cost
 * that only a commercial company is asked for. A housecleaner has no contract
 * to be underwater on, and the tab would open on an empty table beside a
 * notice about a setting they have never been shown.
 */
export const COMMERCIAL_SECTIONS: ReadonlySet<Section> = new Set(['jobCosting']);

/**
 * The tab that opens, and the one anything falls back to. Never a member of
 * COMMERCIAL_SECTIONS, or a residential company would land on a tab it does
 * not have.
 */
export const DEFAULT_SECTION: Section = 'money-received';

export const RANGE_PRESETS = ['last30', 'last90', 'thisYear', 'custom'] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

/** Local calendar date as the API's ISO `YYYY-MM-DD`. */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Today on the location's clock, not the browser's — the API's dates are
 * location-local, and a viewer across midnight from their business would
 * otherwise shift every preset by a day (and `thisYear` by a year on New
 * Year's Eve). Same `en-CA` trick the Worker uses: it formats as YYYY-MM-DD.
 */
export function todayInTimezone(timezone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return toIsoDate(new Date());
  }
}

export function isoDaysBefore(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

/** A 403 PLAN_REQUIRED is an answer, not a flake — same policy as the dashboard. */
export function reportRetry(failureCount: number, error: unknown): boolean {
  return !(error instanceof ApiRequestError && error.status === 403) && failureCount < 2;
}

/** "Aug 4" for chart ticks and week headings. */
export function formatDay(isoDate: string): string {
  return formatCompanyInstant(new Date(`${isoDate}T12:00:00Z`), {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
}

/** "Aug 4, 2026" for table cells. */
export const ReportTimezone = createContext('UTC');
export function useReportDate() {
  const timezone = useContext(ReportTimezone);
  return (iso: string): string =>
    formatCompanyInstant(new Date(iso.includes('T') ? iso : `${iso}T12:00:00Z`), {
      dateStyle: 'medium',
      timeZone: iso.includes('T') ? timezone : 'UTC',
    });
}

/**
 * Commercial-only tabs are not there for a residential company — unless it
 * kept the setting that makes one of them mean something.
 *
 * The endpoint behind job costing does not filter by mode: it aggregates
 * every completed visit in the range, so a company that ran contracts for
 * three years and switched last month still has three years of margins to
 * read. Taking the tab away on the mode alone would put that history out of
 * reach, which is the rule the change orders page and the monthly invoices
 * tab both follow.
 *
 * `laborCostCentsPerHour` is what asks, and it is free: it is already on the
 * session, so nothing is fetched to decide a tab. It is the hourly backup
 * Profit by place uses when Team pay cannot cost a visit, null for every
 * company that has never done commercial work, and set for exactly the
 * company whose history this is protecting. Clearing it in Settings (where
 * it is shown for the same reason) closes the tab.
 */
export function companySeesCommercialReports(company: Company | null): boolean {
  return companyDoesCommercialWork(company) || company?.laborCostCentsPerHour != null;
}

/**
 * The reports this company can open, in SECTIONS order. Without the core
 * reports feature only Money received is left; commercial-only sections are
 * gone for a residential company. The hub, the single-report route, the
 * tabbed page and the command palette all ask this one question.
 */
export function visibleReportSections(company: Company | null, hasReports: boolean): Section[] {
  const commercial = companySeesCommercialReports(company);
  return SECTIONS.filter(
    (value) => (hasReports || value === 'money-received') && (commercial || !COMMERCIAL_SECTIONS.has(value)),
  );
}

/** A report the plan does not carry: still listed, with the plan it needs beside it. */
export function reportSectionLocked(company: Company | null, section: Section): boolean {
  const feature = SECTION_FEATURES[section];
  return feature !== null && !companyHasPlanFeature(company, feature);
}

/** The cheapest plan's name, for a locked report's badge. */
export function reportSectionPlanName(section: Section): string | null {
  const feature = SECTION_FEATURES[section];
  return feature === null ? null : PLANS[minimumPlanFor(feature)].name;
}

/**
 * Until somebody picks a range each report opens on its own default: Job
 * profit on the last 90 days, because its patterns need repeat visits that a
 * month rarely holds; the rest on 30.
 */
export function defaultRangePreset(section: Section): RangePreset {
  return section === 'jobProfit' ? 'last90' : 'last30';
}

/** The `from`/`to` a preset means today on the given clock. */
export function resolveReportRange(
  preset: RangePreset,
  customFrom: string,
  customTo: string,
  timezone: string | undefined,
): RangeParams {
  const today = todayInTimezone(timezone);
  if (preset === 'last30') return { from: isoDaysBefore(today, 29), to: today };
  if (preset === 'last90') return { from: isoDaysBefore(today, 89), to: today };
  if (preset === 'thisYear') return { from: `${today.slice(0, 4)}-01-01`, to: today };
  // Custom: only send what was actually picked; the API completes the rest.
  return { ...(customFrom ? { from: customFrom } : {}), ...(customTo ? { to: customTo } : {}) };
}

export interface RangeParams {
  from?: string;
  to?: string;
}

export function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={classes(
        'px-3 py-1.5 text-sm font-medium',
        active
          ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
          : 'text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]',
      )}
    >
      {children}
    </button>
  );
}

/** The dashboard's stat tile, mirrored — it is not exported from there. */
export function StatTile({ label, value, hint }: { label: string; value: string; hint?: ReactNode }) {
  return (
    <div className="card">
      <p className="text-sm font-medium text-[color:var(--color-muted)]">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular">{value}</p>
      {hint ? <p className="mt-1 text-sm text-[color:var(--color-muted)]">{hint}</p> : null}
    </div>
  );
}

/**
 * Loading, plan-lock and failure handling for one report query — the
 * dashboard's treatment, shared by every section so the seven tabs cannot
 * drift apart on how a locked plan looks.
 */
export function ReportBoundary<T extends PaginatedReport>({
  query,
  children,
  paging,
}: {
  paging?: ReturnType<typeof useReportPaging>;
  query: UseQueryResult<T, unknown>;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();
  const { user } = useSession();

  if (query.isLoading) return <LoadingBlock label={t('reports.loading')} />;
  if (query.isError) {
    const caught = query.error;
    if (caught instanceof ApiRequestError && caught.code === 'PLAN_REQUIRED') {
      const requiredPlan = caught.fields.requiredPlan;
      const planName = isPlanId(requiredPlan)
        ? PLANS[requiredPlan].name
        : PLANS[minimumPlanFor('reports_analytics')].name;
      return (
        <div className="card mx-auto max-w-xl text-center">
          <h2 className="text-xl font-bold">{t('reports.planRequiredTitle')}</h2>
          <p className="mt-3 text-[color:var(--color-muted)]">{t('reports.planRequiredMessage', { plan: planName })}</p>
          {user?.role === 'owner' ? (
            <div className="mt-6">
              <Link to="/settings#settings-billing" className="btn btn-primary">
                {t('reports.planRequiredCta')}
              </Link>
            </div>
          ) : (
            <p className="mt-4 text-sm font-medium">{t('reports.planRequiredAskOwner')}</p>
          )}
        </div>
      );
    }
    return (
      <Alert tone="error" title={t('reports.loadErrorTitle')}>
        <p>{(query.error as Error).message}</p>
        {!(caught instanceof ApiRequestError && caught.status === 403) ? (
          <div className="mt-3">
            <Button className="report-retry" busy={query.isFetching} onClick={() => void query.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : null}
      </Alert>
    );
  }
  if (query.data === undefined) return null;
  return (
    <ReportPagingBoundary paging={paging} report={query.data} busy={query.isFetching}>
      {children(query.data)}
    </ReportPagingBoundary>
  );
}

/** A section heading with the CSV download for the same endpoint beside it. */
export function SectionCard({
  title,
  hint,
  csvHref,
  children,
  collection,
}: {
  title: string;
  hint?: string;
  csvHref?: string;
  collection?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="card work-section">
      <div className="work-section-heading">
        <div>
          <h2>{title}</h2>
          {hint ? <p>{hint}</p> : null}
        </div>
        {csvHref ? (
          <div className="flex flex-wrap gap-2">
            <a href={csvHref} className="btn btn-secondary">
              {t('reports.downloadCsv')}
            </a>
            <a href={csvHref.replace('format=csv', 'format=xlsx')} className="btn btn-secondary">
              {t('reports.downloadXlsx')}
            </a>
          </div>
        ) : null}
      </div>
      {children}
      <ReportPager collection={collection} />
    </section>
  );
}

export function CustomerLink({ id, name }: { id: string; name: string }) {
  // On a table sized to its content, one long name decides the width every
  // other row scrolls through. Customer names run to 160 characters, so the
  // link is bounded rather than left to set the table's width.
  return (
    <Link
      to={`/customers/${id}`}
      title={name}
      className="inline-block max-w-[18rem] table-text align-bottom text-[color:var(--color-brand)] hover:underline"
    >
      {name}
    </Link>
  );
}

export const TH = 'py-2 font-semibold';
export const THR = 'py-2 text-right font-semibold';
export const TD = 'py-2';
export const TDR = 'tabular py-2 text-right';
export const ROW = 'border-b border-[color:var(--color-line)] last:border-0';
